<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue"
import { useRoute, useRouter } from "vue-router"

import Button from "@/components/shared/Button.vue"
import Card from "@/components/shared/Card.vue"
import CardActions from "@/components/shared/CardActions.vue"
import CardContent from "@/components/shared/CardContent.vue"
import CardTitle from "@/components/shared/CardTitle.vue"
import InputBox from "@/components/shared/InputBox.vue"
import InputSelect from "@/components/shared/InputSelect.vue"
import LayoutAuthed from "@/components/shared/LayoutAuthed.vue"
import StatusBadge from "@/components/shared/StatusBadge.vue"
import Table from "@/components/shared/Table.vue"
import TablePaginator from "@/components/shared/TablePaginator.vue"
import { getLocaleMessages } from "@/locales/index"
import type { ListRequest } from "@/services/purchase"
import purchaseService from "@/services/purchase"
import type { Option } from "@/types/common.type"
import type { Purchase } from "@/types/purchase.type"
import { formattedMoney } from "@/utils/money"
import { reportSentry } from "@/utils/sentry"
import { formattedDate } from "@/utils/string"

const route = useRoute()
const router = useRouter()

const orders = ref<Purchase[]>([])
const total = ref(0)

const query = ref<ListRequest>({
  page: (route.query.page as string) || "1",
  perPage: (route.query.perPage as string) || "25",
  code: (route.query.code as string) || "",
  customerCode: (route.query.customerCode as string) || "",
  status: (route.query.status as string) || "",
})

const statusOptions = computed(() => {
  const options = [] as Option[]
  const statusList = getLocaleMessages().order.statusList
  for (const key in statusList) options.push({ value: key, label: statusList[key as keyof typeof statusList] })
  return options
})

// fetch function
async function loadOrders() {
  try {
    const params = Object.entries(query.value).reduce((acc, [key, value]) => {
      acc[key as keyof ListRequest] = value === "" ? null : value
      return acc
    }, {} as ListRequest)

    const data = await purchaseService.list(params)
    orders.value = data.items
    total.value = data.total
  } catch (error) {
    console.error(error)
    reportSentry(error)
  }
}

// initial load
onMounted(async () => {
  await loadOrders()
})

// re-load whenever the query string changes
watch(
  () => route.query,
  (newQuery) => {
    query.value = {
      page: (newQuery.page as string) || "1",
      perPage: (newQuery.perPage as string) || "25",
      code: (newQuery.code as string) || "",
      customerCode: (newQuery.customerCode as string) || "",
      status: (newQuery.status as string) || "",
    }
    loadOrders()
  },
)

// update URL (and trigger the watch) when the form submits
function searchOrders() {
  query.value.page = "1"
  const params = Object.fromEntries(Object.entries(query.value).filter(([_, v]) => v !== ""))

  router.push({ path: route.path, query: params })
}
</script>

<template>
  <LayoutAuthed :title="$t('nav.orders')">
    <Card>
      <CardTitle>{{ $t("shared.search") }}</CardTitle>
      <form @submit.prevent="searchOrders()">
        <CardContent>
          <div class="row">
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputBox v-model="query.code" :label="$t('order.code')" type="text" field="code" />
            </div>
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputBox
                v-model="query.customerCode"
                :label="$t('order.customerCode')"
                type="text"
                field="customerCode"
              />
            </div>
            <div class="col-12 col-md-6 col-xl-3 mb-3">
              <InputSelect v-model="query.status" :label="$t('order.status')" field="status" :options="statusOptions" />
            </div>
          </div>
        </CardContent>
        <CardActions>
          <Button type="submit" :text="$t('shared.search')" />
        </CardActions>
      </form>
    </Card>
    <Card>
      <CardTitle>{{ $t("shared.results") }} ({{ total }})</CardTitle>
      <CardContent>
        <Table>
          <template #header>
            <tr>
              <th>{{ $t("order.code") }}</th>
              <th>{{ $t("order.customerCode") }}</th>
              <th>{{ $t("order.price") }}</th>
              <th>{{ $t("order.orderDate") }}</th>
              <th>{{ $t("order.status") }}</th>
            </tr>
          </template>
          <template #default>
            <tr v-for="(order, index) in orders" :key="index" :class="{ 'new-order': order.status === 'requested' }">
              <td>
                <router-link :to="`/orders/${order.id}`">{{ order.code }}</router-link>
              </td>
              <td>{{ order.customerCode }}</td>
              <td>{{ formattedMoney(order.price, order.currency) }}</td>
              <td>{{ formattedDate(order.createdAt) }}</td>
              <td>
                <StatusBadge model="order" field="status" :value="order.status" />
              </td>
            </tr>
          </template>
        </Table>
        <TablePaginator
          :total="total"
          :perPage="Number(query.perPage)"
          :page="Number(query.page)"
          :url="route.fullPath"
        />
      </CardContent>
    </Card>
  </LayoutAuthed>
</template>

<style scoped>
tr.new-order {
  td {
    background-color: var(--bs-danger-bg-subtle);
  }
}
</style>
